/**
 * Auth Page
 *
 * Single page with toggle between sign-in and sign-up modes.
 * Social-first layout with Google OAuth button prominent at top.
 *
 * Design from CONTEXT.md:
 * - Single page with toggle (Notion-style)
 * - Social-first: Large Google button at top
 * - Minimal sign-up: email + password only
 */

import React, { useState, FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, redirectToGoogleAuth } from '../hooks/useAuth';
import {
  Input,
  Button,
  Divider,
  GoogleIcon,
  ErrorMessage,
  SuccessMessage,
} from '../components/AuthForm';

type AuthMode = 'signin' | 'signup';

export function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check for OAuth error in URL
  const oauthError = searchParams.get('error');

  const {
    signIn,
    signUp,
    isSigningIn,
    isSigningUp,
    signInError,
    signUpError,
  } = useAuth();

  // Form state
  const [mode, setMode] = useState<AuthMode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const isLoading = isSigningIn || isSigningUp;
  const error = mode === 'signin' ? signInError : signUpError;

  // Handle form submission
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSuccessMessage('');

    try {
      if (mode === 'signup') {
        await signUp({ email, password });
        setSuccessMessage('Account created! Check your email to verify.');
        setEmail('');
        setPassword('');
      } else {
        await signIn({ email, password });
        navigate('/');
      }
    } catch (err) {
      // Error is handled by mutation
      console.error('[Auth] Form error:', err);
    }
  };

  // Toggle between modes
  const toggleMode = () => {
    setMode(mode === 'signin' ? 'signup' : 'signin');
    setSuccessMessage('');
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8fafc',
        padding: '20px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '400px',
          backgroundColor: 'var(--card-bg, #ffffff)',
          borderRadius: '16px',
          padding: '40px',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1
            style={{
              fontSize: '28px',
              fontWeight: 700,
              color: 'var(--text-color, #1f2937)',
              marginBottom: '8px',
            }}
          >
            {mode === 'signin' ? 'Welcome back' : 'Create account'}
          </h1>
          <p
            style={{
              color: 'var(--text-muted, #6b7280)',
              fontSize: '14px',
            }}
          >
            {mode === 'signin'
              ? 'Sign in to continue to Deep Agents'
              : 'Get started with Deep Agents'}
          </p>
        </div>

        {/* OAuth Error */}
        {oauthError && (
          <div style={{ marginBottom: '16px' }}>
            <ErrorMessage
              message={
                oauthError === 'oauth_failed'
                  ? 'Google sign-in failed. Please try again.'
                  : 'Something went wrong. Please try again.'
              }
            />
          </div>
        )}

        {/* Error Message */}
        {error && !successMessage && (
          <div style={{ marginBottom: '16px' }}>
            <ErrorMessage message={error} />
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div style={{ marginBottom: '16px' }}>
            <SuccessMessage message={successMessage} />
          </div>
        )}

        {/* Google OAuth Button - PROMINENT */}
        <Button variant="google" onClick={redirectToGoogleAuth} disabled={isLoading}>
          <GoogleIcon />
          Continue with Google
        </Button>

        <Divider text="or" />

        {/* Email/Password Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Input
              type="email"
              name="email"
              placeholder="Email address"
              value={email}
              onChange={setEmail}
              required
              autoComplete={mode === 'signin' ? 'email' : 'email'}
            />

            <Input
              type="password"
              name="password"
              placeholder="Password"
              value={password}
              onChange={setPassword}
              required
              minLength={8}
              autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
            />

            <Button type="submit" variant="primary" loading={isLoading}>
              {mode === 'signin' ? 'Sign in' : 'Create account'}
            </Button>
          </div>
        </form>

        {/* Forgot Password (sign-in only) */}
        {mode === 'signin' && (
          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <a
              href="/forgot-password"
              style={{
                color: 'var(--primary-color, #2563eb)',
                fontSize: '14px',
                textDecoration: 'none',
              }}
            >
              Forgot password?
            </a>
          </div>
        )}

        {/* Toggle Mode */}
        <div
          style={{
            marginTop: '24px',
            paddingTop: '24px',
            borderTop: '1px solid var(--border-color, #e5e7eb)',
            textAlign: 'center',
          }}
        >
          <span style={{ color: 'var(--text-muted, #6b7280)', fontSize: '14px' }}>
            {mode === 'signin' ? "Don't have an account? " : 'Already have an account? '}
          </span>
          <button
            type="button"
            onClick={toggleMode}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--primary-color, #2563eb)',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            {mode === 'signin' ? 'Sign up' : 'Sign in'}
          </button>
        </div>
      </div>
    </div>
  );
}
